import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { X, Check, AlertTriangle, Type, ChevronDown } from 'lucide-react';
import type { FontMatchResult } from '../../lib/svgFontMatcher';
import { useFontStore } from '../../stores/fontStore';
import { supabase } from '../../lib/supabase';
import { loadCustomFont } from '../../lib/fontLoader';

export interface FontAssignment {
  originalCss: string;
  assignedFamily: string | null;
  assignedWeight: number | null;
  assignedStyle: string | null;
  /** cs_fonts row id of the chosen variant (null when unresolved). */
  assignedVariantId?: string | null;
  skipped: boolean;
}

interface FontMatchDialogProps {
  matchResults: FontMatchResult[];
  onConfirm: (assignments: FontAssignment[]) => void;
  onCancel: () => void;
}

const FALLBACK_SAMPLE = 'The quick brown fox jumps over 0123456789';

interface FamilyDropdownProps {
  value: string | null;
  options: string[];
  onChange: (family: string) => void;
}

function FamilyDropdown({ value, options, onChange }: FamilyDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      loadCustomFont(value).catch(() => {});
    }
  }, [value]);

  const filtered = search.trim()
    ? options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(o => !o)}
        className="w-full flex items-center justify-between bg-white/5 border border-white/10 hover:border-white/20 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-colors"
      >
        <span
          className="truncate"
          style={value ? { fontFamily: `"${value}", sans-serif` } : undefined}
        >
          {value || 'Select font family...'}
        </span>
        <ChevronDown size={14} className="text-white/40 flex-shrink-0 ml-2" />
      </button>

      {isOpen && (
        <div className="absolute z-[210] top-full left-0 right-0 mt-1 bg-[#0d0d12] border border-white/10 rounded-lg shadow-2xl max-h-[260px] overflow-hidden flex flex-col">
          <div className="p-1.5 border-b border-white/[0.06]">
            <input
              autoFocus
              type="text"
              placeholder="Search fonts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1 text-xs bg-white/[0.04] border border-white/[0.06] rounded text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/40"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-white/40 text-center">
                {search.trim() ? `No fonts match "${search}"` : 'No fonts uploaded yet'}
              </div>
            ) : (
              filtered.map(family => (
                <button
                  key={family}
                  type="button"
                  onMouseEnter={() => { loadCustomFont(family).catch(() => {}); }}
                  onClick={() => {
                    onChange(family);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-white/[0.05] transition-colors flex items-center justify-between gap-2 ${
                    value === family ? 'bg-cyan-500/10 text-cyan-300' : 'text-white/90'
                  }`}
                  style={{ fontFamily: `"${family}", sans-serif` }}
                >
                  <span className="truncate">{family}</span>
                  {value === family && <Check size={12} className="flex-shrink-0 text-cyan-400" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FontMatchDialog({ matchResults, onConfirm, onCancel }: FontMatchDialogProps) {
  const fonts = useFontStore(state => state.fonts);

  const familyNames = useMemo(() => {
    const families = new Set<string>();
    for (const font of fonts) {
      if (font.isActive) families.add(font.fontFamily);
    }
    return Array.from(families).sort();
  }, [fonts]);

  const variantsMap = useMemo(() => {
    const map = new Map<string, Array<{ weight: number; style: string; id: string }>>();
    for (const font of fonts) {
      if (!font.isActive) continue;
      const existing = map.get(font.fontFamily) || [];
      existing.push({
        weight: parseInt(font.fontWeight, 10) || 400,
        style: font.fontStyle || 'normal',
        id: font.id,
      });
      map.set(font.fontFamily, existing);
    }
    for (const [key, variants] of map.entries()) {
      map.set(key, variants.sort((a, b) => a.weight - b.weight));
    }
    return map;
  }, [fonts]);

  const [assignments, setAssignments] = useState<FontAssignment[]>(() =>
    matchResults.map(result => ({
      originalCss: result.originalCss,
      assignedFamily: result.matchedFamily,
      assignedWeight: result.matchedWeight,
      assignedStyle: result.matchedStyle,
      assignedVariantId: result.matchedVariantId,
      skipped: false,
    }))
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const originals = matchResults.map(r => r.originalCss);
        if (originals.length === 0) return;
        const { data, error } = await supabase
          .from('cs_svg_font_aliases')
          .select('original_css, assigned_family, assigned_weight, assigned_style')
          .in('original_css', originals);
        if (error || !data || cancelled) return;

        const aliasMap = new Map<string, { assigned_family: string; assigned_weight: number | null; assigned_style: string | null }>();
        for (const row of data) aliasMap.set(row.original_css, row);

        setAssignments(prev => prev.map((a) => {
          const alias = aliasMap.get(a.originalCss);
          if (!alias) return a;
          if (!familyNames.includes(alias.assigned_family)) return a;
          const variants = variantsMap.get(alias.assigned_family) || [];
          const matchVariant = variants.find(
            v => v.weight === alias.assigned_weight && v.style === (alias.assigned_style || 'normal')
          );
          const fallbackVariant = variants[0];
          return {
            ...a,
            assignedFamily: alias.assigned_family,
            assignedWeight: matchVariant?.weight ?? alias.assigned_weight ?? fallbackVariant?.weight ?? 400,
            assignedStyle: matchVariant?.style ?? alias.assigned_style ?? fallbackVariant?.style ?? 'normal',
            assignedVariantId: matchVariant?.id ?? fallbackVariant?.id ?? null,
            skipped: false,
          };
        }));
      } catch {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, [matchResults, familyNames, variantsMap]);

  useEffect(() => {
    for (const a of assignments) {
      if (a.assignedFamily) {
        loadCustomFont(a.assignedFamily).catch(() => {});
      }
    }
  }, [assignments]);

  const updateAssignment = useCallback((index: number, updates: Partial<FontAssignment>) => {
    setAssignments(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const handleFamilyChange = useCallback((index: number, family: string) => {
    const variants = variantsMap.get(family) || [];
    const first = variants[0];
    loadCustomFont(family).catch(() => {});
    updateAssignment(index, {
      assignedFamily: family,
      assignedWeight: first?.weight ?? 400,
      assignedStyle: first?.style ?? 'normal',
      assignedVariantId: first?.id ?? null,
      skipped: false,
    });
  }, [variantsMap, updateAssignment]);

  const handleVariantChange = useCallback((index: number, variantStr: string) => {
    const [weightStr, style] = variantStr.split('-');
    const weight = parseInt(weightStr, 10);
    setAssignments(prev => {
      const next = [...prev];
      const current = next[index];
      const variants = current.assignedFamily
        ? variantsMap.get(current.assignedFamily) || []
        : [];
      const match = variants.find((v) => v.weight === weight && v.style === style);
      next[index] = {
        ...current,
        assignedWeight: weight,
        assignedStyle: style,
        assignedVariantId: match?.id ?? null,
      };
      return next;
    });
  }, [variantsMap]);

  const handleSkip = useCallback((index: number) => {
    updateAssignment(index, {
      skipped: true,
      assignedFamily: null,
      assignedWeight: null,
      assignedStyle: null,
      assignedVariantId: null,
    });
  }, [updateAssignment]);

  const handleUnskip = useCallback((index: number) => {
    const result = matchResults[index];
    updateAssignment(index, {
      skipped: false,
      assignedFamily: result.matchedFamily,
      assignedWeight: result.matchedWeight,
      assignedStyle: result.matchedStyle,
      assignedVariantId: result.matchedVariantId,
    });
  }, [matchResults, updateAssignment]);

  const persistAliases = useCallback(async (finalAssignments: FontAssignment[]) => {
    try {
      const payload = finalAssignments
        .filter(a => !a.skipped && a.assignedFamily && a.assignedWeight !== null)
        .map(a => ({
          original_css: a.originalCss,
          assigned_family: a.assignedFamily!,
          assigned_weight: a.assignedWeight,
          assigned_style: a.assignedStyle || 'normal',
          updated_at: new Date().toISOString(),
        }));
      if (payload.length === 0) return;
      await supabase
        .from('cs_svg_font_aliases')
        .upsert(payload, { onConflict: 'original_css' });
    } catch (err) {
      console.warn('[FontMatch] Failed to persist font aliases:', err);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    persistAliases(assignments);
    onConfirm(assignments);
  }, [assignments, onConfirm, persistAliases]);

  const allResolved = assignments.every(a => a.skipped || (a.assignedFamily && a.assignedWeight !== null));

  const autoMatchedCount = matchResults.filter(r => r.matched && !r.needsManualVariantSelection).length;
  const needsAttentionCount = matchResults.filter(r => !r.matched || r.needsManualVariantSelection).length;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl w-[720px] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Type className="w-5 h-5 text-cyan-400" />
            <div>
              <h2 className="text-white font-semibold text-lg">SVG Font Matching</h2>
              <p className="text-white/50 text-xs mt-0.5">
                {autoMatchedCount > 0 && (
                  <span className="text-emerald-400">{autoMatchedCount} auto-matched</span>
                )}
                {autoMatchedCount > 0 && needsAttentionCount > 0 && ' \u00b7 '}
                {needsAttentionCount > 0 && (
                  <span className="text-amber-400">{needsAttentionCount} need attention</span>
                )}
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="text-white/40 hover:text-white/80 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {assignments.map((assignment, index) => {
            const result = matchResults[index];
            const variants = assignment.assignedFamily
              ? variantsMap.get(assignment.assignedFamily) || []
              : [];
            const previewText = (result.sampleText && result.sampleText.length > 0)
              ? result.sampleText
              : FALLBACK_SAMPLE;

            return (
              <div
                key={result.originalCss}
                className={`rounded-lg border p-4 transition-colors ${
                  assignment.skipped
                    ? 'border-white/5 bg-white/[0.02] opacity-50'
                    : result.matched && !result.needsManualVariantSelection
                    ? 'border-emerald-500/20 bg-emerald-500/[0.03]'
                    : 'border-amber-500/20 bg-amber-500/[0.03]'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {result.matched && !result.needsManualVariantSelection ? (
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    )}
                    <code className="text-white/70 text-sm font-mono bg-white/5 px-2 py-0.5 rounded truncate">
                      {result.originalCss}
                    </code>
                    {result.matchTier && (
                      <span className="text-[10px] text-white/30 uppercase tracking-wider flex-shrink-0">
                        {result.matchTier}
                      </span>
                    )}
                    {result.matched && !result.needsManualVariantSelection && result.matchedWeight && (
                      <span className="text-xs text-white/40 flex-shrink-0">
                        {result.matchedWeight}{result.matchedStyle === 'italic' ? 'i' : ''}
                      </span>
                    )}
                  </div>
                  {assignment.skipped ? (
                    <button
                      onClick={() => handleUnskip(index)}
                      className="text-xs text-cyan-400 hover:text-cyan-300 flex-shrink-0"
                    >
                      Undo skip
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSkip(index)}
                      className="text-xs text-white/30 hover:text-white/60 flex-shrink-0"
                    >
                      Skip
                    </button>
                  )}
                </div>

                {!assignment.skipped && (
                  <>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex-1 min-w-0">
                        <FamilyDropdown
                          value={assignment.assignedFamily}
                          options={familyNames}
                          onChange={(family) => handleFamilyChange(index, family)}
                        />
                      </div>

                      {assignment.assignedFamily && variants.length > 1 && (
                        <div className="w-48 flex-shrink-0">
                          <select
                            value={
                              assignment.assignedWeight !== null
                                ? `${assignment.assignedWeight}-${assignment.assignedStyle || 'normal'}`
                                : ''
                            }
                            onChange={(e) => handleVariantChange(index, e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 appearance-none cursor-pointer"
                          >
                            <option value="" className="bg-[#1a1a2e]">Select variant...</option>
                            {variants.map(v => (
                              <option
                                key={`${v.weight}-${v.style}`}
                                value={`${v.weight}-${v.style}`}
                                className="bg-[#1a1a2e]"
                              >
                                {v.weight} {v.style !== 'normal' ? v.style : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {assignment.assignedFamily && variants.length === 1 && (
                        <span className="text-xs text-white/40 w-48 flex-shrink-0">
                          {variants[0].weight} {variants[0].style !== 'normal' ? variants[0].style : ''}
                        </span>
                      )}
                    </div>

                    {assignment.assignedFamily && assignment.assignedWeight !== null && (
                      <div className="mt-3 px-3 py-2.5 rounded-md bg-black/20 border border-white/5">
                        <div className="text-[10px] uppercase tracking-wider text-white/30 mb-1">
                          Preview
                        </div>
                        <div
                          className="text-white text-xl leading-tight truncate"
                          style={{
                            fontFamily: `"${assignment.assignedFamily}", sans-serif`,
                            fontWeight: assignment.assignedWeight,
                            fontStyle: assignment.assignedStyle === 'italic' ? 'italic' : 'normal',
                          }}
                          title={previewText}
                        >
                          {previewText}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
          <p className="text-white/30 text-xs">
            Skipped fonts will use browser fallback rendering
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-white/60 hover:text-white/90 transition-colors"
            >
              Cancel Import
            </button>
            <button
              onClick={handleConfirm}
              disabled={!allResolved}
              className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors ${
                allResolved
                  ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                  : 'bg-white/5 text-white/30 cursor-not-allowed'
              }`}
            >
              Apply & Import
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
